import { apiRequest } from '../lib/httpClient'
import { PlatformType } from '../types/authorization'
import type {
  AccountMappingRow,
  AccountMappingTemplate,
  AccountsMasterRow,
  AdvisoryMappingDetails,
  AdvisoryMappingListRow,
  AdvisoryMappingUploadInput,
  MultiEntityOwnershipRow,
  RelatedContactRow,
  RelatedLoanAccountRow,
  UserAccessRow,
} from '../types/advisoryMapping'
import { asArray, pickNumber, pickString } from './shared/coercion'

function mapPlatformLabel(platformType: number): string {
  switch (platformType) {
    case PlatformType.Xero:
      return 'Xero'
    case PlatformType.QuickBooks:
      return 'QuickBooks'
    case PlatformType.Myob:
      return 'MYOB'
    case PlatformType.XeroXpm:
      return 'Xero XPM'
    case PlatformType.XeroWorkflowMax:
      return 'Xero WorkflowMax'
    case PlatformType.WorkGuru:
      return 'WorkGuru'
    case PlatformType.BRWFM:
      return 'BRWFM'
    case PlatformType.Karbon:
      return 'Karbon'
    case PlatformType.FYI:
      return 'FYI'
    default:
      return 'Unknown'
  }
}

function mapListRow(row: Record<string, unknown>): AdvisoryMappingListRow {
  const platformType = pickNumber(row['platformType'] ?? row['PlatformType'])
  return {
    platformId:
      pickString(row['platformId']) || pickString(row['PlatformId']) || pickString(row['id']) || pickString(row['Id']),
    organisationName: pickString(row['organisationName'] ?? row['OrganisationName'] ?? row['name'] ?? row['Name']),
    group: pickString(row['group'] ?? row['Group'] ?? row['groupName'] ?? row['GroupName']),
    platformType,
    platformLabel: mapPlatformLabel(platformType),
  }
}

function mapAccountTemplate(row: Record<string, unknown>): AccountMappingTemplate {
  const accountType = pickString(row['accountType'] ?? row['AccountType'])
  const reportCodeName = pickString(row['reportCodeName'] ?? row['ReportCodeName'])
  return {
    account: pickString(row['account'] ?? row['Account']),
    accountType,
    reportCodeName,
    atRcn: pickString(row['atRcn'] ?? row['AtRcn']) || (accountType && reportCodeName ? `${accountType} (${reportCodeName})` : accountType),
    plGroup: pickString(row['plGroup'] ?? row['PlGroup']),
    balanceSheetType: pickString(row['balanceSheetType'] ?? row['BalanceSheetType']),
    accountMappingId: pickString(row['accountMappingId'] ?? row['AccountMappingId']),
  }
}

function mapAccountRow(row: Record<string, unknown>): AccountMappingRow {
  return {
    ...row,
    account: pickString(row['account'] ?? row['Account']),
    accountType: pickString(row['accountType'] ?? row['AccountType']),
    reportCodeName: pickString(row['reportCodeName'] ?? row['ReportCodeName']),
    atRcn: pickString(row['atRcn'] ?? row['AtRcn']),
    plGroup: pickString(row['plGroup'] ?? row['PlGroup']),
    balanceSheetType: pickString(row['balanceSheetType'] ?? row['BalanceSheetType']),
    accountMappingId: pickString(row['accountMappingId'] ?? row['AccountMappingId']),
    tenant: pickString(row['tenant'] ?? row['Tenant']),
    isDeleted: Boolean(row['isDeleted'] ?? row['IsDeleted']),
    isModified: Boolean(row['isModified'] ?? row['IsModified']),
    isNew: Boolean(row['isNew'] ?? row['IsNew']),
    isOverriden: pickNumber(row['isOverriden'] ?? row['IsOverriden'], 0),
  }
}

function mapAccountsMaster(row: Record<string, unknown>): AccountsMasterRow {
  const accountType = pickString(row['accountType'] ?? row['AccountType'])
  const reportCodeName = pickString(row['reportCodeName'] ?? row['ReportCodeName'])
  return {
    accountType,
    reportCodeName,
    atRcn: pickString(row['atRcn'] ?? row['AtRcn']) || (accountType && reportCodeName ? `${accountType} (${reportCodeName})` : accountType),
    plGroup: pickString(row['plGroup'] ?? row['PlGroup']),
    balanceSheetType: pickString(row['balanceSheetType'] ?? row['BalanceSheetType']),
    cashflowType: pickString(row['cashflowType'] ?? row['CashflowType']),
  }
}

function mapMultiEntity(row: Record<string, unknown>): MultiEntityOwnershipRow {
  return {
    ...row,
    shareholderName: pickString(row['shareholderName'] ?? row['ShareholderName']),
    shareholderEmail: pickString(row['shareholderEmail'] ?? row['ShareholderEmail']),
    shares: pickNumber(row['shares'] ?? row['Shares'], 0),
    startDate: pickString(row['startDate'] ?? row['StartDate']) || null,
    endDate: pickString(row['endDate'] ?? row['EndDate']) || null,
    multiEntityOwnershipId: pickString(row['multiEntityOwnershipId'] ?? row['MultiEntityOwnershipId']),
    tenant: pickString(row['tenant'] ?? row['Tenant']),
    isDeleted: Boolean(row['isDeleted'] ?? row['IsDeleted']),
    isModified: Boolean(row['isModified'] ?? row['IsModified']),
    isNew: Boolean(row['isNew'] ?? row['IsNew']),
  }
}

function mapRelatedContact(row: Record<string, unknown>): RelatedContactRow {
  return {
    ...row,
    contactName: pickString(row['contactName'] ?? row['ContactName'] ?? row['relatedContact'] ?? row['RelatedContact']),
    tenant: pickString(row['tenant'] ?? row['Tenant']),
    isDeleted: Boolean(row['isDeleted'] ?? row['IsDeleted']),
    isModified: Boolean(row['isModified'] ?? row['IsModified']),
    isNew: Boolean(row['isNew'] ?? row['IsNew']),
  }
}

function mapRelatedLoanAccount(row: Record<string, unknown>): RelatedLoanAccountRow {
  return {
    ...row,
    loanAccount: pickString(row['loanAccount'] ?? row['LoanAccount']),
    tenant: pickString(row['tenant'] ?? row['Tenant']),
    isDeleted: Boolean(row['isDeleted'] ?? row['IsDeleted']),
    isModified: Boolean(row['isModified'] ?? row['IsModified']),
    isNew: Boolean(row['isNew'] ?? row['IsNew']),
  }
}

function mapUserAccess(row: Record<string, unknown>): UserAccessRow {
  return {
    ...row,
    name: pickString(row['name'] ?? row['Name']),
    email: pickString(row['email'] ?? row['Email']),
    canAccess: Boolean(row['canAccess'] ?? row['CanAccess']),
    isXeroUser: Boolean(row['isXeroUser'] ?? row['IsXeroUser']),
    isModified: Boolean(row['isModified'] ?? row['IsModified']),
  }
}

export async function fetchAdvisoryMappingList(clientId: string): Promise<AdvisoryMappingListRow[]> {
  const payload = await apiRequest<unknown>('advisorymapping/ui/list', {
    method: 'GET',
    query: { clientId },
  })

  const rows = asArray<Record<string, unknown>>(payload)
  return rows.map(mapListRow)
}

export async function fetchAdvisoryMappingDetails(platformId: string, clientId: string): Promise<AdvisoryMappingDetails> {
  const [details, relatedLoanAccountList] = await Promise.all([
    apiRequest<Record<string, unknown>>('advisorymapping/ui/details', {
      method: 'GET',
      query: { platformId, clientId },
    }),
    apiRequest<Array<Record<string, unknown> | string>>('advisorymapping/ui/advisoryMapping/relatedLoanAccountList', {
      method: 'GET',
      query: { platformId, clientId },
    }),
  ])

  const loanList = asArray<{ value: string }>(relatedLoanAccountList).map((item) => item.value).filter(Boolean)

  return {
    organisationName: pickString(details['organisationName'] ?? details['OrganisationName']),
    accountMapping: asArray<Record<string, unknown>>(details['accountMapping'] ?? details['AccountMapping']).map(mapAccountRow),
    multiEntityOwnership: asArray<Record<string, unknown>>(details['multiEntityOwnership'] ?? details['MultiEntityOwnership']).map(mapMultiEntity),
    relatedContact: asArray<Record<string, unknown>>(details['relatedContact'] ?? details['RelatedContact']).map(mapRelatedContact),
    relatedLoanAccount: asArray<Record<string, unknown>>(details['relatedLoanAccount'] ?? details['RelatedLoanAccount']).map(mapRelatedLoanAccount),
    userAccess: asArray<Record<string, unknown>>(details['userAccess'] ?? details['UserAccess']).map(mapUserAccess),
    accountMappingList: asArray<Record<string, unknown>>(details['accountMappingList'] ?? details['AccountMappingList']).map(mapAccountTemplate),
    accountsMaster: asArray<Record<string, unknown>>(details['accountsMaster'] ?? details['AccountsMaster']).map(mapAccountsMaster),
    relatedLoanAccountList: loanList,
  }
}

export async function updateAdvisoryMappingGroup(platformId: string, groupName: string) {
  return apiRequest('advisorymapping/ui/advisoryMapping/updateGroup', {
    method: 'POST',
    body: { platformId, groupName },
  })
}

export async function upsertAccountMapping(accountMapping: AccountMappingRow[]) {
  return apiRequest('advisorymapping/ui/accountMapping/upsert', {
    method: 'POST',
    body: { accountMapping },
  })
}

export async function upsertMultiEntityOwnership(multiEntityOwnership: MultiEntityOwnershipRow[]) {
  return apiRequest('advisorymapping/ui/multiEntityOwnership/upsert', {
    method: 'POST',
    body: { multiEntityOwnership },
  })
}

export async function upsertRelatedContacts(relatedContact: RelatedContactRow[]) {
  return apiRequest('advisorymapping/ui/advisoryMapping/upsertRelatedContacts', {
    method: 'POST',
    body: {
      relatedContact: relatedContact.map((row) => ({
        ...row,
        contactName: row.contactName,
        relatedContact: row.contactName,
      })),
    },
  })
}

export async function upsertRelatedLoanAccounts(relatedLoanAccount: RelatedLoanAccountRow[]) {
  return apiRequest('advisorymapping/ui/advisoryMapping/upsertRelatedLoanAccounts', {
    method: 'POST',
    body: { relatedLoanAccount },
  })
}

export async function upsertUserAccess(userAccess: UserAccessRow[]) {
  return apiRequest('advisorymapping/ui/userAccess/upsert', {
    method: 'POST',
    body: { userAccess },
  })
}

export async function uploadAdvisoryMappingData(input: AdvisoryMappingUploadInput) {
  return apiRequest('advisorymapping/ui/upload', {
    method: 'POST',
    body: input,
  })
}

async function exportCsv(path: string, platformId: string) {
  return apiRequest<string>(path, {
    method: 'GET',
    query: { platformId },
  })
}

export async function exportAdvisoryMapping(platformId: string) {
  return exportCsv('advisorymapping/ui/advisoryMapping/export', platformId)
}

export async function exportAccountMapping(platformId: string) {
  return exportCsv('advisorymapping/ui/accountMapping/export', platformId)
}

export async function exportMultiEntityOwnership(platformId: string) {
  return exportCsv('advisorymapping/ui/multiEntityOwnership/export', platformId)
}

export async function exportRelatedContact(platformId: string) {
  return exportCsv('advisorymapping/ui/relatedContact/export', platformId)
}

export async function exportRelatedLoanAccount(platformId: string) {
  return exportCsv('advisorymapping/ui/relatedLoanAccount/export', platformId)
}
